import React, { useState, useEffect } from 'react';
import { logger } from '../utils/logger';
import { 
  Card, 
  Typography, 
  Button, 
  Alert, 
  Modal, 
  Input, 
  Dropdown,
  Space,
  Row,
  Col,
  Spin,
  message,
  Table,
  Tabs
} from 'antd';
import { 
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
  SwapOutlined,
  BarChartOutlined,
  LineChartOutlined,
  DollarOutlined,
  StockOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { API_CONFIG, apiClient } from '../api/config';
import CompanySearch from '../components/CompanySearch';
import activityTracker from './ActivityTracker';
import { 
  basicInfoColumns,
  growthValuationColumns,
  financialRatiosColumns,
  pricePerformanceColumns,
  trendColumns,
  pageNames
} from '../components/TableColumns';
import TechnicalChart from '../pages/TechnicalChart';

// 專門為 WatchlistPage 使用的技術圖表組件
const WatchlistTechnicalChartsPage = ({ screeningResults }) => {
  // 過濾出有價格數據的股票
  const stocksWithPriceData = screeningResults.filter(stock => 
    stock.stock_price_data && stock.stock_price_data.length > 0
  );

  // 沒有數據的情況
  if (!screeningResults || screeningResults.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <Typography.Text type="secondary" style={{ fontSize: '16px', display: 'block', marginBottom: '8px' }}>
          您的收藏列表為空
        </Typography.Text>
        <Typography.Text type="secondary" style={{ fontSize: '14px' }}>
          請瀏覽公司信息並將您感興趣的公司添加至收藏
        </Typography.Text>
      </div>
    );
  }

  // 有數據但沒有價格數據的情況
  if (stocksWithPriceData.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <Typography.Text type="secondary" style={{ fontSize: '16px', display: 'block', marginBottom: '8px' }}>
          無法獲取技術分析數據
        </Typography.Text>
        <Typography.Text type="secondary" style={{ fontSize: '14px', display: 'block', marginBottom: '4px' }}>
          共 {screeningResults.length} 隻收藏股票，但暫時無法獲取價格數據
        </Typography.Text>
        <Typography.Text type="secondary" style={{ fontSize: '14px' }}>
          請稍後再試或聯繫管理員
        </Typography.Text>
      </div>
    );
  }

  return (
    <Row gutter={[16, 16]}>
      {stocksWithPriceData.map((stock, index) => (
        <Col xs={24} sm={12} md={8} lg={6} key={stock.company_symbol}>
          <Card 
            style={{ 
              height: '220px',
              border: '1px solid #e8e8e8',
              borderRadius: '8px'
            }}
            bodyStyle={{ padding: '8px', height: '100%' }}
          >
            <TechnicalChart 
              symbol={stock.company_symbol}
              priceData={stock.stock_price_data}
              width="100%"
              height="200px"
            />
          </Card>
        </Col>
      ))}
    </Row>
  );
};

const WatchlistPage = () => {
  const { isAuthenticated, user } = useAuth();
  const [watchlist, setWatchlist] = useState([]);
  const [detailedData, setDetailedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tableViewType, setTableViewType] = useState('basic');
  
  // 分组相关状态
  const [watchlistGroups, setWatchlistGroups] = useState([]);
  const [currentGroup, setCurrentGroup] = useState('default');
  const [showAddGroupDialog, setShowAddGroupDialog] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  
  // 新增公司相关状态
  const [showAddCompanyDialog, setShowAddCompanyDialog] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  
  // 移动股票相关状态
  const [moveMenuAnchor, setMoveMenuAnchor] = useState(null);
  const [moveTargetStock, setMoveTargetStock] = useState(null);
  
  logger.log("WatchlistPage认证状态:", { isAuthenticated, user });

  useEffect(() => {
    activityTracker.trackPageView('watchlist');
    
    if (isAuthenticated && user) {
      fetchWatchlistGroups();
      fetchWatchlist();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  // 获取分组列表
  const fetchWatchlistGroups = async () => {
    try {
      const response = await apiClient.get(`${API_CONFIG.endpoints.watchlist.list}?user_id=${user.user_id}`);
      setWatchlistGroups(response.data);
      logger.log('獲取分組列表成功:', response.data);
    } catch (err) {
      logger.error('獲取分組列表失敗:', err);
      setError('獲取分組列表失敗: ' + (err.message || '未知錯誤'));
    }
  };

  const fetchWatchlist = async (groupName = currentGroup) => {
    try {
      setLoading(true);
      setError('');
      
      // 第一步：獲取指定分組的收藏列表
      const response = await apiClient.get(`${API_CONFIG.endpoints.watchlist.show}?user_id=${user.user_id}&group=${groupName}`);
      const watchlistData = response.data;
      setWatchlist(watchlistData);
      
      if (watchlistData.length > 0) {
        // 第二步：調用篩選 API 獲取詳細數據
        await fetchDetailedData(watchlistData);
      } else {
        setDetailedData([]);
      }
    } catch (err) {
      logger.error('獲取收藏列表失敗:', err);
      setError('獲取收藏列表失敗: ' + (err.message || '未知錯誤'));
    } finally {
      setLoading(false);
    }
  };

  const fetchDetailedData = async (watchlistData) => {
    try {
      // 提取股票代碼列表
      const symbols = watchlistData.map(item => item.symbol);
      
      // 調用篩選 API，傳入股票代碼列表
      const filterParams = new URLSearchParams();
      symbols.forEach(symbol => {
        filterParams.append('symbolList[]', symbol);
      });
      
      const filterResponse = await apiClient.get(`/api/screener/filter?${filterParams.toString()}`);
      setDetailedData(filterResponse.data);
      
      logger.info('成功獲取詳細數據，股票數量:', filterResponse.data.length);
    } catch (err) {
      logger.error('獲取詳細數據失敗:', err);
      setError('獲取詳細數據失敗: ' + (err.message || '未知錯誤'));
    }
  };

  const removeFromWatchlist = async (symbolId) => {
    try {
      if (!user || !user.user_id) {
        setError('用戶未登錄或用戶信息不完整');
        return;
      }

      // 根据symbolId找到对应的symbol
      const itemToRemove = detailedData.find(item => 
        item.id === symbolId || item.company_symbol === symbolId
      );
      
      if (!itemToRemove) {
        setError('未找到要移除的股票');
        return;
      }

      await apiClient.delete(API_CONFIG.endpoints.watchlist.remove, {
        data: {
          symbol: itemToRemove.company_symbol,
          group: currentGroup
        }
      });
      
      // 重新获取当前分组的数据
      await fetchWatchlist(currentGroup);
      
      message.success('已從收藏列表中移除');
    } catch (err) {
      logger.error('移除收藏項失敗:', err);
      setError('移除收藏項失敗: ' + (err.response?.data?.message || err.message || '未知錯誤'));
    }
  };

  const handleTableViewChange = (viewType) => {
    activityTracker.trackBtnExecution('watchlist', `table_view_${viewType}`, `表格視圖_${viewType}`);
    setTableViewType(viewType);
  };

  // 切换分组
  const handleGroupChange = (groupName) => {
    setCurrentGroup(groupName);
    fetchWatchlist(groupName);
  };

  // 创建新分组
  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      message.error('請輸入分組名稱');
      return;
    }

    try {
      await apiClient.post(API_CONFIG.endpoints.watchlist.add, {
        group: newGroupName.trim()
      });

      message.success('分組創建成功');
      setNewGroupName('');
      setShowAddGroupDialog(false);
      
      // 重新获取分组列表
      await fetchWatchlistGroups();
    } catch (err) {
      logger.error('創建分組失敗:', err);
      message.error('創建分組失敗: ' + (err.response?.data?.message || err.message || '未知錯誤'));
    }
  };

  // 处理公司选择（仅保存选中的公司）
  const handleCompanySelect = (selectedCompany) => {
    if (!selectedCompany || !selectedCompany.symbol) {
      message.error('請選擇有效的公司');
      return;
    }
    setSelectedCompany(selectedCompany);
  };

  // 确认添加公司到收藏列表
  const handleConfirmAddCompany = async () => {
    try {
      if (!selectedCompany) {
        message.error('請先選擇公司');
        return;
      }

      await apiClient.post(API_CONFIG.endpoints.watchlist.add, {
        symbol: selectedCompany.symbol,
        group: currentGroup,
        user_id: user.user_id
      });

      message.success(`已将 ${selectedCompany.symbol} 添加到 ${currentGroup} 分组`);
      setShowAddCompanyDialog(false);
      setSelectedCompany(null);
      
      // 重新获取当前分组的数据
      await fetchWatchlist(currentGroup);
    } catch (err) {
      logger.error('添加公司到收藏列表失敗:', err);
      message.error('添加失敗: ' + (err.response?.data?.message || err.message || '未知錯誤'));
    }
  };

  // 关闭对话框时清理状态
  const handleCloseAddCompanyDialog = () => {
    setShowAddCompanyDialog(false);
    setSelectedCompany(null);
  };

  // 处理移动股票按钮点击
  const handleMoveClick = (event, stock) => {
    setMoveMenuAnchor(event.currentTarget);
    setMoveTargetStock(stock);
  };

  // 关闭移动选单
  const handleCloseMoveMenu = () => {
    setMoveMenuAnchor(null);
    setMoveTargetStock(null);
  };

  // 移动股票到指定分组
  const handleMoveToGroup = async (targetGroup) => {
    try {
      if (!moveTargetStock || !targetGroup) {
        message.error('移動操作失敗，缺少必要信息');
        return;
      }

      // 先从当前分组移除
      await apiClient.delete(API_CONFIG.endpoints.watchlist.remove, {
        data: {
          symbol: moveTargetStock.company_symbol,
          group: currentGroup,
          user_id: user.user_id
        }
      });

      // 添加到目标分组
      await apiClient.post(API_CONFIG.endpoints.watchlist.add, {
        symbol: moveTargetStock.company_symbol,
        group: targetGroup,
        user_id: user.user_id
      });

      message.success(`已将 ${moveTargetStock.company_symbol} 移动到 ${targetGroup} 分组`);
      
      // 关闭选单
      handleCloseMoveMenu();
      
      // 重新获取当前分组的数据
      await fetchWatchlist(currentGroup);
    } catch (err) {
      logger.error('移動股票失敗:', err);
      message.error('移動失敗: ' + (err.response?.data?.message || err.message || '未知錯誤'));
    }
  };

  const getColumns = () => {
    // 自定義操作列 - 移到最前面，只顯示圖標
    const customActionColumn = {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'left',
      render: (_, record) => {
        // 防護措施：確保 record 不為 null/undefined
        if (!record) {
          return <span style={{ color: '#999' }}>N/A</span>;
        }
        
        return (
          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
            <Button
              component={Link}
              to={`/business-product?symbol=${record.company_symbol}`}
              icon={<EyeOutlined />}
              type="primary"
              size="small"
              onClick={() => activityTracker.trackBtnExecution('watchlist', 'view_company', `查看公司_${record.company_symbol}`)}
              style={{ 
                minWidth: 0, 
                padding: '4px', 
                borderRadius: '50%',
                width: '32px',
                height: '32px'
              }}
              title="查看"
            />
            <Button
              icon={<SwapOutlined />}
              type="default"
              size="small"
              onClick={(e) => handleMoveClick(e, record)}
              style={{ 
                minWidth: 0, 
                padding: '4px',
                borderRadius: '50%',
                width: '32px',
                height: '32px'
              }}
              title="移動到其他分組"
            />
            <Button
              icon={<DeleteOutlined />}
              type="primary"
              danger
              size="small"
              onClick={() => removeFromWatchlist(record.id || record.company_symbol)}
              style={{ 
                minWidth: 0, 
                padding: '4px',
                borderRadius: '50%',
                width: '32px',
                height: '32px'
              }}
              title="移除"
            />
          </div>
        );
      }
    };

    // 直接使用基礎列，不調用 getColumnsByType 避免自動添加操作列
    let baseColumns;
    switch (tableViewType) {
      case 'basic':
        baseColumns = basicInfoColumns;
        break;
      case 'growth':
        baseColumns = growthValuationColumns;
        break;
      case 'financial':
        baseColumns = financialRatiosColumns;
        break;
      case 'price':
        baseColumns = pricePerformanceColumns;
        break;
      case 'trend':
        baseColumns = trendColumns;
        break;
      case 'technical':
        baseColumns = basicInfoColumns; // 暫時使用基本資訊列，之後可以添加技術圖表列
        break;
      default:
        baseColumns = basicInfoColumns;
    }
    
    // 排除watchlist操作列（因為在WatchlistPage中不需要）
    const filteredColumns = (baseColumns || []).filter(col => col && col.key !== 'watchlist_action');
    
    // 操作列放在最前面，不添加最後的操作列
    return [customActionColumn, ...filteredColumns];
  };

  if (!isAuthenticated || !user) {
    return (
      <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
        <Alert
          message="請先登錄以查看您的收藏列表"
          type="warning"
          showIcon
        />
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '24px',
      maxWidth: '1400px',
      margin: '0 auto',
      backgroundColor: '#f5f5f5',
      minHeight: '100vh'
    }}>
      {/* 横向显示的收藏列表 */}
      <Card style={{ marginBottom: '24px', borderRadius: '8px' }}>
        <div style={{ padding: '16px' }}>
          <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
            <Typography.Title level={5} style={{ margin: 0 }}>
              收藏列表
            </Typography.Title>
            <Button
              icon={<PlusOutlined />}
              size="small"
              type="dashed"
              onClick={() => setShowAddGroupDialog(true)}
            >
              新增列表
            </Button>
          </Row>
          
          <Space wrap>
            {watchlistGroups.map((group) => (
              <Button
                key={group.group}
                type={currentGroup === group.group ? "primary" : "default"}
                size="small"
                onClick={() => handleGroupChange(group.group)}
                style={{
                  minWidth: '80px',
                  height: '32px',
                  fontSize: '12px',
                  borderRadius: '16px'
                }}
              >
                {group.group}
              </Button>
            ))}
          </Space>
        </div>
      </Card>

      {/* 主要内容区域 */}
      <Card style={{ borderRadius: '8px', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)' }}>
        <div style={{ padding: '24px' }}>
          <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
            <Typography.Title level={3} style={{ margin: 0 }}>
              我的收藏 - {currentGroup}
            </Typography.Title>
            <Button
              icon={<PlusOutlined />}
              type="primary"
              onClick={() => setShowAddCompanyDialog(true)}
              style={{ fontSize: '14px' }}
            >
              新增公司代碼
            </Button>
          </Row>
          
          {error && <Alert message={error} type="error" showIcon style={{ marginBottom: '16px' }} />}
          
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin size="large" />
            </div>
          ) : watchlist.length === 0 ? (
            <Alert 
              message="您的收藏列表為空。請瀏覽公司信息並將您感興趣的公司添加至收藏。"
              type="info" 
              showIcon 
              style={{ margin: '16px 0' }} 
            />
          ) : (
            <div style={{ marginTop: '20px' }}>
              {/* 表格视图导航栏 */}
              <Tabs
                activeKey={tableViewType}
                onChange={handleTableViewChange}
                items={[
                  {
                    key: 'basic',
                    label: (
                      <span>
                        <AppstoreOutlined /> {pageNames.basic}
                      </span>
                    ),
                  },
                  {
                    key: 'financial',
                    label: (
                      <span>
                        <BarChartOutlined /> {pageNames.financial}
                      </span>
                    ),
                  },
                  {
                    key: 'growth',
                    label: (
                      <span>
                        <LineChartOutlined /> {pageNames.growth}
                      </span>
                    ),
                  },
                  {
                    key: 'price',
                    label: (
                      <span>
                        <StockOutlined /> {pageNames.price}
                      </span>
                    ),
                  },
                  {
                    key: 'trend',
                    label: (
                      <span>
                        <DollarOutlined /> {pageNames.trend}
                      </span>
                    ),
                  },
                  {
                    key: 'technical',
                    label: (
                      <span>
                        <BarChartOutlined /> {pageNames.technical}
                      </span>
                    ),
                  },
                ]}
                style={{ marginBottom: '20px' }}
              />

                          {/* 技術分析圖表視圖 */}
              {tableViewType === 'technical' && (
                <div style={{ padding: '20px' }}>
                  <div style={{ marginBottom: '20px' }}>
                    <Typography.Title level={4} style={{ marginBottom: '12px' }}>
                      技術分析圖表
                    </Typography.Title>
                    <Typography.Text type="secondary">
                      共 {detailedData.length} 隻收藏股票，其中 {detailedData.filter(stock => 
                        stock.stock_price_data && stock.stock_price_data.length > 0
                      ).length} 隻有技術分析數據 （紅色均線為MA100）
                    </Typography.Text>
                  </div>
                  <WatchlistTechnicalChartsPage screeningResults={detailedData} />
                </div>
              )}
              
              {/* 表格視圖 */}
              {tableViewType !== 'technical' && (
                <Table
                  columns={getColumns()}
                  dataSource={detailedData || []}
                  rowKey={(record) => record?.company_symbol || record?.symbol || Math.random()}
                  loading={loading}
                  size="small"
                  bordered
                  pagination={{
                    defaultPageSize: 20,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showQuickJumper: true,
                    showTotal: (total, range) => `第 ${range[0]}-${range[1]} 項，共 ${total} 項`,
                    position: ['bottomCenter']
                  }}
                  scroll={{ 
                    x: 1200, // 設置固定寬度，當內容超過時會顯示水平滾動條
                    y: 600    // 設置垂直滾動高度
                  }}
                  style={{ 
                    overflowX: 'scroll',
                    overflowY: 'scroll'
                  }}
                  tableLayout="fixed"
                  className="stock-screener-table watchlist-table"
                  rowClassName={(record, index) => index % 2 === 0 ? 'even-row' : 'odd-row'}
                />
              )}
            </div>
          )}
        </div>
      </Card>

      {/* 新增分组对话框 */}
      <Modal
        title="新增收藏列表"
        open={showAddGroupDialog}
        onCancel={() => setShowAddGroupDialog(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowAddGroupDialog(false)}>
            取消
          </Button>,
          <Button key="submit" type="primary" onClick={handleCreateGroup}>
            確定
          </Button>
        ]}
        destroyOnClose
      >
        <Input
          autoFocus
          placeholder="請輸入收藏列表名稱"
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
          size="large"
          onPressEnter={handleCreateGroup}
        />
      </Modal>

      {/* 新增公司对话框 */}
      <Modal
        title={`新增公司到 ${currentGroup} 分組`}
        open={showAddCompanyDialog}
        onCancel={handleCloseAddCompanyDialog}
        footer={[
          <Button key="cancel" onClick={handleCloseAddCompanyDialog}>
            取消
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            onClick={handleConfirmAddCompany}
            disabled={!selectedCompany}
          >
            確定
          </Button>
        ]}
        destroyOnClose
        width={500}
        style={{ top: 20 }}
        bodyStyle={{ 
          padding: '24px',
          minHeight: '200px',
          overflow: 'visible'
        }}
      >
        <div style={{ marginBottom: '16px' }}>
          <Typography.Text type="secondary">
            請輸入公司名稱或股票代碼進行搜尋
          </Typography.Text>
        </div>
        <div style={{ position: 'relative', zIndex: 10000, marginBottom: '24px' }}>
          <CompanySearch onSelect={handleCompanySelect} />
        </div>
        
        {/* 显示选中的公司信息 */}
        {selectedCompany && (
          <div style={{ 
            marginTop: '24px', 
            padding: '16px', 
            border: '1px solid #e0e0e0', 
            borderRadius: '6px',
            backgroundColor: '#f9f9f9'
          }}>
            <Typography.Text strong style={{ color: '#1890ff', display: 'block', marginBottom: '8px' }}>
              已選擇的公司：
            </Typography.Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {selectedCompany.image && (
                <img 
                  src={selectedCompany.image} 
                  alt={selectedCompany.company_name}
                  style={{ width: 32, height: 32, borderRadius: 4 }}
                  onError={(e) => {
                    if (e.target) {
                      e.target.style.display = 'none';
                    }
                  }}
                />
              )}
              <div>
                <Typography.Text strong style={{ display: 'block', marginBottom: '4px' }}>
                  {selectedCompany.symbol}
                </Typography.Text>
                <Typography.Text type="secondary">
                  {selectedCompany.company_name}
                </Typography.Text>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* 移动股票选单 */}
      <Dropdown
        open={Boolean(moveMenuAnchor)}
        onOpenChange={(open) => !open && handleCloseMoveMenu()}
        trigger={[]}
        overlay={
          <div style={{
            background: 'white',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            boxShadow: '0 3px 6px -4px rgba(0,0,0,.12), 0 6px 16px 0 rgba(0,0,0,.08), 0 9px 28px 8px rgba(0,0,0,.05)',
            maxHeight: '300px',
            width: '200px',
            overflow: 'auto'
          }}>
            {watchlistGroups
              .filter(group => group.group !== currentGroup) // 排除当前分组
              .map((group) => (
                <div
                  key={group.group}
                  onClick={() => handleMoveToGroup(group.group)}
                  style={{
                    fontSize: '14px',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f0f0f0',
                    transition: 'all 0.2s',
                    ':hover': {
                      backgroundColor: '#1890ff',
                      color: 'white',
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (e.target) {
                      e.target.style.backgroundColor = '#1890ff';
                      e.target.style.color = 'white';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (e.target) {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = 'inherit';
                    }
                  }}
                >
                  移動到 "{group.group}"
                </div>
              ))}
            {watchlistGroups.filter(group => group.group !== currentGroup).length === 0 && (
              <div style={{ 
                fontSize: '14px', 
                fontStyle: 'italic',
                padding: '8px 16px',
                color: '#999',
                borderBottom: '1px solid #f0f0f0'
              }}>
                沒有其他分組可移動
              </div>
            )}
          </div>
        }
      />

      <style jsx global>{`
        /* 表格滾動條樣式 */
        .watchlist-table {
          overflow-x: scroll !important;
          overflow-y: scroll !important;
        }
        
        .watchlist-table .ant-table-wrapper {
          overflow-x: scroll !important;
        }
        
        .watchlist-table .ant-table {
          overflow-x: scroll !important;
        }
        
        .watchlist-table .ant-table-content {
          overflow-x: scroll !important;
        }
        
        /* 自定義滾動條樣式 */
        .watchlist-table::-webkit-scrollbar {
          height: 12px;
          width: 12px;
        }
        
        .watchlist-table::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 6px;
        }
        
        .watchlist-table::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 6px;
        }
        
        .watchlist-table::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
        
        /* 確保表格容器有足夠的寬度 */
        .watchlist-table .ant-table-container {
          min-width: 1200px;
        }
        
        /* 強制顯示滾動條 */
        .watchlist-table .ant-table-body {
          overflow-x: scroll !important;
          overflow-y: scroll !important;
        }
        
        /* 確保表格有最小高度以觸發垂直滾動條 */
        .watchlist-table .ant-table {
          min-height: 400px;
        }
        
        /* 強制顯示水平滾動條 */
        .watchlist-table .ant-table-content {
          min-width: 1200px;
        }
        
        @media screen and (max-width: 480px) {
          /* 手機模式下的滾動條樣式 */
          .watchlist-table::-webkit-scrollbar {
            height: 8px;
            width: 8px;
          }
        }
      `}</style>
    </div>
  );
};

export default WatchlistPage;
